import type React from 'react';

// Constellation
import { Button, ButtonGroup, DropdownPopper, TextButton } from '@zillow/constellation';

// Components
import ActiveRentals from '../ActiveRentals';
import Amenities from '../Amenities';
import Bedrooms from '../Bedrooms';
import Distance from '../Distance';
import PropertyType from '../PropertyType';
import OffMarket from '../OffMarket';

// Misc / Utils
import * as S from './styles';
import { getActiveFilters } from '../../utils/filterUtils';
import { useAppSelector } from '../../flux/configureStore';
import { augmentError, formatForLog } from '../../utils/errors';
import { logger } from '../../index';

// Types
import type { PartialFilterType } from '../../models/Types';

interface FilterTrayProps {
  activeRentalsLabel: string;
  offMarketLabel: string;
  bedroomsLabel: string;
  propertyTypesLabel: string;
  distanceLabel: string;
  amenitiesLabel: string;
  setIsLoading: (isLoading: boolean) => void;
}

const FilterTray: React.FunctionComponent<React.PropsWithChildren<React.PropsWithChildren<FilterTrayProps>>> = ({
  activeRentalsLabel,
  offMarketLabel,
  bedroomsLabel,
  propertyTypesLabel,
  distanceLabel,
  amenitiesLabel,
  setIsLoading,
}) => {
  const setFilter = useAppSelector((state) => state.setFilter);
  const filter = useAppSelector((state) => state.filter);
  const hasActiveFilters = Boolean(Object.keys(getActiveFilters(filter)).length);
  const { active, inactive } = filter.activeTypes;

  const onFilterChange = async (partialFilterObj?: PartialFilterType) => {
    setIsLoading(true);
    try {
      await setFilter(partialFilterObj);
    } catch (err: any) {
      augmentError(err, {
        root: 'modules/FilterTray#onFilterChange',
      });

      logger.error(formatForLog(err), 'Filter: Error handling filter change in filter Tray');
    }
    setIsLoading(false);
  };

  // Reset all filters
  const handleResetFilters = async () => {
    setIsLoading(true);
    try {
      await setFilter();
    } catch (err) {
      augmentError(err, {
        root: 'components/FilterTray#handleResetFilters',
      });

      logger.error(formatForLog(err), 'Filter: Error handling filter price change');
    }
    setIsLoading(false);
  };

  // component passed as triggered prop must be called functionally (no jsx)
  return (
    <ButtonGroup marginBottom="sm" marginX="sm" aria-label="Filters">
      <DropdownPopper triggered={ActiveRentals({ onFilterChange })}>
        <Button size="sm" onClick={() => {}}>
          {active && <S.Dot activeType="forRent" />}
          {activeRentalsLabel}
        </Button>
      </DropdownPopper>
      <DropdownPopper triggered={OffMarket({ onFilterChange })}>
        <Button size="sm" onClick={() => {}}>
          {inactive && <S.Dot activeType="offMarket" />}
          {offMarketLabel}
        </Button>
      </DropdownPopper>
      <DropdownPopper triggered={Bedrooms({ onFilterChange })}>
        <Button size="sm" onClick={() => {}}>
          {bedroomsLabel}
        </Button>
      </DropdownPopper>
      <DropdownPopper triggered={PropertyType({ onFilterChange })}>
        <Button size="sm" onClick={() => {}}>
          {propertyTypesLabel}
        </Button>
      </DropdownPopper>
      <DropdownPopper triggered={Distance({ onFilterChange })}>
        <Button size="sm" onClick={() => {}}>
          {distanceLabel}
        </Button>
      </DropdownPopper>
      <DropdownPopper triggered={Amenities({ onFilterChange })}>
        <Button size="sm" onClick={() => {}}>
          {amenitiesLabel}
        </Button>
      </DropdownPopper>
      {hasActiveFilters && <TextButton onClick={handleResetFilters}>Reset all</TextButton>}
    </ButtonGroup>
  );
};

export default FilterTray;
